import { z } from 'zod';
import { SocialNetworkEnum } from '../types/sources.types';

const SOURCE_TYPE_VALUES = [
  'MediosAbiertos',
  'MediosSuscripcion',
  'RedesSociales',
  'GoogleTrends',
  'Comunicados',
] as const;

const baseSourceSchema = z.object({
  // react-doctor-disable-next-line react-doctor/zod-v4-no-deprecated-error-customization -- z.string('msg')/z.number('msg') is valid Zod 4 string-shorthand for the error message, not the deprecated {required_error} object
  name: z
    .string('El nombre de la fuente es requerido.')
    .min(1, 'El nombre de la fuente es requerido.')
    .max(255, 'El nombre no puede exceder 255 caracteres.'),

  type: z.enum(SOURCE_TYPE_VALUES),

  // react-doctor-disable-next-line react-doctor/zod-v4-no-deprecated-error-customization -- z.string('msg')/z.number('msg') is valid Zod 4 string-shorthand for the error message, not the deprecated {required_error} object
  updateIntervalHours: z
    .number('El intervalo es requerido.')
    .int('El intervalo debe ser un número entero.')
    .min(1, 'El intervalo debe ser mínimo 1 hora.'),

  countryIds: z.array(z.string()).optional(),
  selector: z.number().optional(),

  regionIds: z.array(z.string()).min(1, 'Debe seleccionar al menos una región.'),

  isEnabled: z.boolean(),
});

/**
 * Schema for Medios Abiertos source type
 * Requires: baseUrl
 */
export const openMediaSourceSchema = baseSourceSchema.extend({
  type: z.literal('MediosAbiertos'),
  // react-doctor-disable-next-line react-doctor/zod-v4-no-deprecated-error-customization -- z.string('msg')/z.number('msg') is valid Zod 4 string-shorthand for the error message, not the deprecated {required_error} object
  baseUrl: z
    .string('La URL es requerida.')
    .min(1, 'La URL es requerida.')
    .url('La URL debe ser válida.'),
});

/**
 * Schema for Medios Suscripción source type
 * Requires: baseUrl, username, password
 */
export const subscriptionMediaSourceSchema = baseSourceSchema.extend({
  type: z.literal('MediosSuscripcion'),
  // react-doctor-disable-next-line react-doctor/zod-v4-no-deprecated-error-customization -- z.string('msg')/z.number('msg') is valid Zod 4 string-shorthand for the error message, not the deprecated {required_error} object
  baseUrl: z
    .string('La URL es requerida.')
    .min(1, 'La URL es requerida.')
    .url('La URL debe ser válida.'),
  // react-doctor-disable-next-line react-doctor/zod-v4-no-deprecated-error-customization -- z.string('msg')/z.number('msg') is valid Zod 4 string-shorthand for the error message, not the deprecated {required_error} object
  username: z.string('El usuario es requerido.').min(1, 'El usuario es requerido.'),
  // react-doctor-disable-next-line react-doctor/zod-v4-no-deprecated-error-customization -- z.string('msg')/z.number('msg') is valid Zod 4 string-shorthand for the error message, not the deprecated {required_error} object
  password: z.string('La contraseña es requerida.').min(1, 'La contraseña es requerida.'),
});

/**
 * Schema for Redes Sociales source type
 * Requires: network (the social platform the source represents)
 */
export const socialMediaSourceSchema = baseSourceSchema.extend({
  type: z.literal('RedesSociales'),
  network: z.nativeEnum(SocialNetworkEnum).optional(),
});

/**
 * Schema for Google Trends source type
 * Requires: keywords (array with at least 1 item)
 * No URL or credentials needed
 */
export const googleTrendsSourceSchema = baseSourceSchema.extend({
  type: z.literal('GoogleTrends'),
  keywords: z
    .array(z.string().min(1, 'El término no puede estar vacío.'))
    .min(1, 'Debe ingresar al menos un término de búsqueda.'),
});

/**
 * Schema for Comunicados source type
 * Requires: baseUrl
 */
export const communicatesSourceSchema = baseSourceSchema.extend({
  type: z.literal('Comunicados'),
  // react-doctor-disable-next-line react-doctor/zod-v4-no-deprecated-error-customization -- z.string('msg')/z.number('msg') is valid Zod 4 string-shorthand for the error message, not the deprecated {required_error} object
  baseUrl: z
    .string('La URL es requerida.')
    .min(1, 'La URL es requerida.')
    .url('La URL debe ser válida.'),
});

/**
 * Schema for creating a source.
 * Uses a single object with conditional superRefine instead of a discriminated union
 * so that friendly error messages surface even when `type` is undefined (a union would
 * emit a generic "Invalid input" and skip the refinement entirely).
 */
export const createSourceSchema = z
  .object({
    name: z
      .string('El nombre de la fuente es requerido.')
      .min(1, 'El nombre de la fuente es requerido.')
      .max(255, 'El nombre no puede exceder 255 caracteres.'),
    type: z.enum(SOURCE_TYPE_VALUES).optional(),
    updateIntervalHours: z
      .number('El intervalo es requerido.')
      .int('El intervalo debe ser un número entero.')
      .min(1, 'El intervalo debe ser mínimo 1 hora.'),
    countryIds: z.array(z.string()).optional(),
    selector: z.number().optional(),
    regionIds: z.array(z.string()).min(1, 'Debe seleccionar al menos una región.'),
    isEnabled: z.boolean(),
    baseUrl: z.string().optional(),
    username: z.string().optional(),
    password: z.string().optional(),
    network: z.nativeEnum(SocialNetworkEnum).optional(),
    keywords: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'El tipo de fuente es requerido.',
        path: ['type'],
      });
      return;
    }

    switch (data.type) {
      case 'MediosAbiertos':
        if (!data.baseUrl) {
          ctx.addIssue({ code: 'custom', message: 'La URL es requerida.', path: ['baseUrl'] });
        } else {
          const urlResult = z.string().url().safeParse(data.baseUrl);
          if (!urlResult.success) {
            ctx.addIssue({ code: 'custom', message: 'La URL debe ser válida.', path: ['baseUrl'] });
          }
        }
        break;
      case 'MediosSuscripcion':
        if (!data.baseUrl) {
          ctx.addIssue({ code: 'custom', message: 'La URL es requerida.', path: ['baseUrl'] });
        } else {
          const urlResult = z.string().url().safeParse(data.baseUrl);
          if (!urlResult.success) {
            ctx.addIssue({ code: 'custom', message: 'La URL debe ser válida.', path: ['baseUrl'] });
          }
        }
        if (!data.username) {
          ctx.addIssue({ code: 'custom', message: 'El usuario es requerido.', path: ['username'] });
        }
        if (!data.password) {
          ctx.addIssue({ code: 'custom', message: 'La contraseña es requerida.', path: ['password'] });
        }
        break;
      case 'RedesSociales':
        if (data.network === undefined) {
          ctx.addIssue({ code: 'custom', message: 'La red social es requerida.', path: ['network'] });
        }
        break;
      case 'GoogleTrends':
        if (!data.keywords || data.keywords.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: 'Debe ingresar al menos un término de búsqueda.',
            path: ['keywords'],
          });
        }
        break;
      case 'Comunicados':
        if (!data.baseUrl) {
          ctx.addIssue({ code: 'custom', message: 'La URL es requerida.', path: ['baseUrl'] });
        } else {
          const urlResult = z.string().url().safeParse(data.baseUrl);
          if (!urlResult.success) {
            ctx.addIssue({ code: 'custom', message: 'La URL debe ser válida.', path: ['baseUrl'] });
          }
        }
        break;
    }
  });

export type CreateSourceFormValues = z.infer<typeof createSourceSchema>;

/**
 * Edit schemas — credentials are optional since they may not be changed
 */
const editBaseSourceSchema = baseSourceSchema;

export const editOpenMediaSourceSchema = editBaseSourceSchema.extend({
  type: z.literal('MediosAbiertos'),
  // react-doctor-disable-next-line react-doctor/zod-v4-no-deprecated-error-customization -- z.string('msg')/z.number('msg') is valid Zod 4 string-shorthand for the error message, not the deprecated {required_error} object
  baseUrl: z
    .string('La URL es requerida.')
    .min(1, 'La URL es requerida.')
    .url('La URL debe ser válida.'),
  username: z.string().optional(),
  password: z.string().optional(),
  network: z.nativeEnum(SocialNetworkEnum).optional(),
  keywords: z.array(z.string()).optional(),
});

export const editSubscriptionMediaSourceSchema = editBaseSourceSchema.extend({
  type: z.literal('MediosSuscripcion'),
  // react-doctor-disable-next-line react-doctor/zod-v4-no-deprecated-error-customization -- z.string('msg')/z.number('msg') is valid Zod 4 string-shorthand for the error message, not the deprecated {required_error} object
  baseUrl: z
    .string('La URL es requerida.')
    .min(1, 'La URL es requerida.')
    .url('La URL debe ser válida.'),
  username: z.string().optional(),
  password: z.string().optional(),
  network: z.nativeEnum(SocialNetworkEnum).optional(),
  keywords: z.array(z.string()).optional(),
});

export const editSocialMediaSourceSchema = editBaseSourceSchema.extend({
  type: z.literal('RedesSociales'),
  network: z.nativeEnum(SocialNetworkEnum).optional(),
  baseUrl: z.string().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  keywords: z.array(z.string()).optional(),
});

export const editGoogleTrendsSourceSchema = editBaseSourceSchema.extend({
  type: z.literal('GoogleTrends'),
  keywords: z
    .array(z.string().min(1, 'El término no puede estar vacío.'))
    .min(1, 'Debe ingresar al menos un término de búsqueda.'),
  baseUrl: z.string().optional(),
  username: z.string().optional(),
  password: z.string().optional(),
  network: z.nativeEnum(SocialNetworkEnum).optional(),
});

export const editCommunicatesSourceSchema = editBaseSourceSchema.extend({
  type: z.literal('Comunicados'),
  // react-doctor-disable-next-line react-doctor/zod-v4-no-deprecated-error-customization -- z.string('msg')/z.number('msg') is valid Zod 4 string-shorthand for the error message, not the deprecated {required_error} object
  baseUrl: z
    .string('La URL es requerida.')
    .min(1, 'La URL es requerida.')
    .url('La URL debe ser válida.'),
  username: z.string().optional(),
  password: z.string().optional(),
  network: z.nativeEnum(SocialNetworkEnum).optional(),
  keywords: z.array(z.string()).optional(),
});

export const editSourceSchema = z
  .union([
    editOpenMediaSourceSchema,
    editSubscriptionMediaSourceSchema,
    editSocialMediaSourceSchema,
    editGoogleTrendsSourceSchema,
    editCommunicatesSourceSchema,
  ])
  .superRefine((data, ctx) => {
    if (data.type === 'RedesSociales' && data.network === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'La red social es requerida.',
        path: ['network'],
      });
    }
  });

export type EditSourceFormValues = z.infer<typeof editSourceSchema>;
